import type { FC } from 'react';
import { useEffect } from 'react';
import { z } from 'zod';
import type { OperatorsResponse } from 'src/apis/iip-service/types';
import { SearchableMultiSelect } from 'src/components/form/select/searchable-multi-select';
import { formOptionSchema } from 'src/components/form/zod-schemas';
import { useAxios } from 'src/hooks/use-axios';
import { useClientSearch } from 'src/hooks/use-client-search';

type Props = {
  label: string;
  url: string;
  placeholder: string;
  name?: string;
};

export const operatorOptionSchema = z.object({
  shortName: z.string().optional(),
  ...formOptionSchema.shape,
});

type OperatorOption = z.input<typeof operatorOptionSchema>;

export const OperatorSearchableMultiSelect: FC<Props> = ({
  label,
  url,
  placeholder,
  name = 'operatorId',
}) => {
  const searchRequest = useAxios(
    (axios, baseConfig) =>
      axios.request<OperatorsResponse>({
        ...baseConfig,
        url,
      }),
    { neededOnPageLoad: false }
  );

  const operators = searchRequest.response?.data._embedded.operators.map(
    (operator) =>
      ({
        value: operator.id,
        label: operator.name,
        shortName: operator.shortName,
      }) satisfies OperatorOption
  );

  const search = useClientSearch(
    operators,
    (operator, search) =>
      operator.label.toLowerCase().match(search) ||
      operator.shortName?.toLowerCase().match(search)
  );

  // on the very first search input we try to fetch the initial list
  useEffect(() => {
    if (!search.searchValue || searchRequest.response) return;
    searchRequest.execute();
    // oxlint-disable-next-line exhaustive-deps
  }, [search.searchValue, searchRequest.response]);

  return (
    <SearchableMultiSelect
      label={label}
      name={name}
      stacked
      placeholder={placeholder}
      {...search}
      renderOptionHint={(option) => option.shortName}
      renderSelectedOptionTooltip={(option) => (
        <>
          <strong>Short Name:</strong> {option.shortName}
        </>
      )}
    />
  );
};
